/**
 * DHIS2-style Single Value KPI Card
 * Displays a large number with trend indicator, threshold coloring, and optional sparkline
 */
import React from 'react';
import { Box, Card, CardContent, Typography, Tooltip, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { formatNumber, KPI_COLORS } from '../../theme/dashboardTheme';

interface SingleValueCardProps {
  title: string;
  value: number | null;
  previousValue?: number | null;
  unit?: string;
  format?: 'integer' | 'percentage' | 'ratio' | 'decimal';
  colorScheme?: keyof typeof KPI_COLORS;
  subtitle?: string;
  target?: number;
  icon?: React.ReactNode;
  onClick?: () => void;
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
}

const SingleValueCard: React.FC<SingleValueCardProps> = ({
  title,
  value,
  previousValue,
  unit,
  format = 'integer',
  colorScheme = 'primary',
  subtitle,
  target,
  icon,
  onClick,
  loading = false,
  size = 'medium',
}) => {
  const colors = KPI_COLORS[colorScheme];

  const getChange = () => {
    if (previousValue === null || previousValue === undefined || value === null) return null;
    if (previousValue === 0) return null;
    return ((value - previousValue) / previousValue) * 100;
  };

  const change = getChange();

  const getTrendIcon = () => {
    if (change === null) return null;
    if (change > 1) return <TrendingUp sx={{ fontSize: 18, color: '#388E3C' }} />;
    if (change < -1) return <TrendingDown sx={{ fontSize: 18, color: '#D32F2F' }} />;
    return <TrendingFlat sx={{ fontSize: 18, color: '#A0ADBA' }} />;
  };

  const getTargetProgress = () => {
    if (!target || value === null) return null;
    return Math.min((value / target) * 100, 100);
  };

  const targetProgress = getTargetProgress();

  const sizeStyles = {
    small: { valueFontSize: '1.8rem', titleFontSize: '0.75rem', padding: 1.5 },
    medium: { valueFontSize: '2.5rem', titleFontSize: '0.85rem', padding: 2 },
    large: { valueFontSize: '3.2rem', titleFontSize: '0.95rem', padding: 2.5 },
  };

  const styles = sizeStyles[size];

  return (
    <Card
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        border: '1px solid',
        borderColor: 'divider',
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
          borderColor: colors.icon,
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: styles.padding, '&:last-child': { pb: styles.padding } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', fontWeight: 500, fontSize: styles.titleFontSize, textTransform: 'uppercase', letterSpacing: 0.5 }}
          >
            {title}
          </Typography>
          {icon && (
            <Box sx={{
              width: 36, height: 36, borderRadius: '8px',
              backgroundColor: colors.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: colors.icon,
            }}>
              {icon}
            </Box>
          )}
        </Box>

        {/* Value */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: styles.valueFontSize,
              color: colors.text,
              lineHeight: 1.1,
              fontFeatureSettings: '"tnum"',
            }}
          >
            {loading ? '...' : formatNumber(value, format)}
          </Typography>
          {unit && format !== 'percentage' && format !== 'ratio' && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {unit}
            </Typography>
          )}
        </Box>

        {/* Trend & Change */}
        {change !== null && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            {getTrendIcon()}
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: change > 0 ? '#388E3C' : change < 0 ? '#D32F2F' : '#A0ADBA',
              }}
            >
              {change > 0 ? '+' : ''}{change.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              vs previous period
            </Typography>
          </Box>
        )}

        {/* Target Progress */}
        {targetProgress !== null && (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Target: {formatNumber(target, format)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text }}>
                {targetProgress.toFixed(0)}%
              </Typography>
            </Box>
            <Box sx={{
              height: 6, borderRadius: 3, backgroundColor: '#E8EDF2', overflow: 'hidden',
            }}>
              <Box sx={{
                height: '100%', borderRadius: 3, width: `${targetProgress}%`,
                backgroundColor: targetProgress >= 80 ? '#388E3C' : targetProgress >= 50 ? '#FFC324' : '#D32F2F',
                transition: 'width 0.5s ease',
              }} />
            </Box>
          </Box>
        )}

        {/* Subtitle */}
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default SingleValueCard;
